
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery } from "@apollo/client";
import { DELETE_CANDIDATE, ARCHIVE_CANDIDATE } from "../../../GraphQL/candidates/mutations";
import { GET_CANDIDATE_BY_ID } from "../../../GraphQL/candidates/queries";
import { EToastColorStatus, EToastPosition } from "../../../interfaces";

interface IProps {
  id: number | null;
  show: boolean;
  onHideModal: () => void;
  reloadCandidates: () => void;
  onCreateToast?: (
    color: EToastColorStatus,
    position: EToastPosition,
    message: string,
  ) => void;
}

const CandidateDeleteModal = ({id, show, onHideModal, reloadCandidates, onCreateToast = () => {}}: IProps) => {
  const { loading, data } = useQuery(GET_CANDIDATE_BY_ID, { variables: { id: Number(id) }});

  const [deleteCandidate] = useMutation(DELETE_CANDIDATE, {
    onCompleted(){
      reloadCandidates();
      onHideModal();
      onCreateToast(
        EToastColorStatus.SUCCESS,
        EToastPosition.BOTTOM_END,
        "Candidate deleted!",
      );
    },
    onError(error: any){
      const errorMessage = error?.networkError?.result?.errors[0]?.message;
      onHideModal();
      onCreateToast(
        EToastColorStatus.FAILED,
        EToastPosition.BOTTOM_END,
        errorMessage,
      );
    },
  });

  const [archiveCandidate] = useMutation(ARCHIVE_CANDIDATE, {
    onCompleted(){
      reloadCandidates();
      onHideModal();
      onCreateToast(
        EToastColorStatus.SUCCESS,
        EToastPosition.BOTTOM_END,
        "Candidate archived!",
      );
    },
  });

  const handleDeleteCandidate = () => {
    if (id === null) return
    deleteCandidate({
      variables: {
        candidateId: Number(id),
      },
    });
  }

  const handleArchiveCandidate = () => {
    if (id === null) return
    archiveCandidate({
      variables: {
        candidateId: `${id}`,
      },
    });
  }

  if (loading) {
    return (
      <Dialog open={show} onOpenChange={(open) => !open && onHideModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Loading...</DialogTitle>
          </DialogHeader>
          <div>Loading...</div>
        </DialogContent>
      </Dialog>
    )
  };

  if (data?.candidates?.items[0]?.matches > 0) {
    return (
      <Dialog open={show} onOpenChange={(open) => !open && onHideModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Candidate</DialogTitle>
          </DialogHeader>
          <div>
            This candidate can not be deleted due to associated matches.
            Do you wish to archive this candidate instead?
            Archived candidates are hidden from your list of candidates.
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={onHideModal}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleArchiveCandidate}>
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHideModal()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Candidate</DialogTitle>
        </DialogHeader>
        <div>
          Are you sure you want to delete this candidate? This process cannot be undone.
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onHideModal}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDeleteCandidate}>Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CandidateDeleteModal;
